<template>
  <div class="pm-canvas">
    <div ref="map" class="pm-map" />
    <div class="pm-map-tools">
      <b-button
        size="sm"
        variant="light"
        :title="$t('partner-map.map.zoom-in')"
        @click="zoom(1)"
      >
        <b-icon-plus />
      </b-button>
      <b-button
        size="sm"
        variant="light"
        :title="$t('partner-map.map.zoom-out')"
        @click="zoom(-1)"
      >
        <b-icon-dash />
      </b-button>
      <b-button
        size="sm"
        variant="light"
        :title="$t('partner-map.map.reset')"
        @click="resetView"
      >
        <b-icon-arrows-angle-contract />
      </b-button>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import { BIconPlus, BIconDash, BIconArrowsAngleContract } from 'bootstrap-vue';
import { HERO_R_100, heroDiamond, heroFillR, shortName } from './model';

// Germany, comfortably framed. Used for the initial view and the reset button.
const GERMANY_BOUNDS = L.latLngBounds([47.2, 5.8], [55.1, 15.1]);

export default {
  name: 'PartnerMapCanvas',
  components: { BIconPlus, BIconDash, BIconArrowsAngleContract },
  props: {
    /** Partner groups currently in the ranking — drawn as sized dots. */
    partners: { type: Array, required: true },
    /** Everything else with a location — drawn as small grey dots. */
    restPartners: { type: Array, required: true },
    heroes: { type: Array, required: true },
    selection: { type: Object, default: null },
    metric: { type: String, default: 'revenue_all' },
    query: { type: String, default: '' },
    showPartners: { type: Boolean, default: true },
    showHeroes: { type: Boolean, default: true },
    showRest: { type: Boolean, default: true },
    showRadius: { type: Boolean, default: false },
    showLabels: { type: Boolean, default: false },
    showRatings: { type: Boolean, default: false },
    /** (partner) => tooltip HTML. Injected so this component stays presentation-only. */
    partnerTooltip: { type: Function, required: true },
    /** (hero) => tooltip HTML. */
    heroTooltip: { type: Function, required: true },
    /** Locale-bound formatters from model.js `formatters()`. */
    fmt: { type: Object, required: true },
  },
  data() {
    return { map: null, userMoved: false };
  },
  computed: {
    selectionKey() {
      const s = this.selection;
      return s ? `${s.kind}:${s.ref.id || s.ref.name}` : null;
    },
    // A single object so one watcher covers every input that changes the drawing.
    drawKey() {
      return [
        this.partners,
        this.restPartners,
        this.heroes,
        this.selection,
        this.metric,
        this.query,
        this.showPartners,
        this.showHeroes,
        this.showRest,
        this.showRadius,
        this.showLabels,
        this.showRatings,
      ];
    },
  },
  watch: {
    drawKey() {
      this.draw();
    },
    // Keyed, not object-identity: the partner objects are rebuilt on every filter change
    // and panning on each of those would fight the user for control of the viewport.
    selectionKey(key) {
      if (!key || !this.selection.ref || this.selection.ref.lat == null) return;
      this.panTo(this.selection.ref);
    },
  },
  mounted() {
    this.map = L.map(this.$refs.map, {
      zoomControl: false,
      attributionControl: true,
      preferCanvas: false,
      // Scroll-zoom without a modifier hijacks page scrolling in a long dashboard.
      scrollWheelZoom: 'center',
    });
    this.map.fitBounds(GERMANY_BOUNDS);
    // Once the user pans or zooms, a later resize must not snap their view back.
    this.map.on('zoomstart dragstart', () => {
      this.userMoved = true;
    });

    // OpenStreetMap's own tiles — no third-party basemap account needed. The CSS filter
    // in this component desaturates them so the data marks stay the loudest thing here.
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap',
    }).addTo(this.map);

    this.radiusLayer = L.layerGroup().addTo(this.map);
    this.markerLayer = L.layerGroup().addTo(this.map);
    this.labelLayer = L.layerGroup().addTo(this.map);
    this.draw();

    // The card is still laying out when `mounted` fires, so Leaflet measures a container
    // that is not its final size and fits the bounds to the wrong viewport. Re-measure once
    // the layout settles, and again whenever the column is resized.
    this.$nextTick(() => this.refit());
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.refit());
      this.resizeObserver.observe(this.$refs.map);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    /** Re-measure the container; keep the default framing until the user moves the map. */
    refit() {
      if (!this.map) return;
      this.map.invalidateSize({ animate: false });
      if (!this.userMoved)
        this.map.fitBounds(GERMANY_BOUNDS, { animate: false });
    },
    zoom(dir) {
      if (this.map) this.map.setZoom(this.map.getZoom() + dir);
    },
    resetView() {
      this.userMoved = false;
      if (this.map) this.map.fitBounds(GERMANY_BOUNDS);
    },
    panTo(o) {
      if (!this.map) return;
      // Don't yank the map around when the point is already comfortably in view.
      if (this.map.getBounds().pad(-0.15).contains([o.lat, o.lon])) return;
      this.map.panTo([o.lat, o.lon]);
    },
    matches(o) {
      if (!this.query) return true;
      const q = this.query.toLowerCase();
      return (
        String(o.name || '')
          .toLowerCase()
          .includes(q) ||
        String(o.city || '')
          .toLowerCase()
          .includes(q)
      );
    },
    metricValue(p) {
      if (this.metric === 'uniform') return 1;
      return Math.max(0, p[this.metric] || 0);
    },
    // Area-proportional: a dot twice the area means twice the value.
    dotRadius(p, maxValue) {
      if (this.metric === 'uniform') return 6;
      return 4 + 15 * Math.sqrt(this.metricValue(p) / (maxValue || 1));
    },
    heroIcon(hero) {
      const u = hero.util ? hero.util.util : null;
      const size = 2 * (HERO_R_100 + 3);
      const c = size / 2;
      const frame = `<path d="${heroDiamond(
        c,
        c,
        HERO_R_100,
      )}" class="pm-hero-frame${u == null ? ' pm-hero-nodata' : ''}"/>`;
      const fill =
        u == null
          ? ''
          : `<path d="${heroDiamond(c, c, heroFillR(u))}" class="pm-hero-fill${
              u > 1 ? ' over' : ''
            }"/>`;
      return L.divIcon({
        className: `pm-hero-icon${this.matches(hero) ? '' : ' pm-dim'}`,
        iconSize: [size, size],
        iconAnchor: [c, c],
        html: `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">${frame}${fill}</svg>`,
      });
    },
    labelFor(marker, text) {
      marker.bindTooltip(text, {
        permanent: true,
        direction: 'top',
        className: 'pm-label',
        opacity: 1,
      });
    },
    draw() {
      if (!this.map) return;
      this.radiusLayer.clearLayers();
      this.markerLayer.clearLayers();
      this.labelLayer.clearLayers();

      const ranked = this.partners.filter((p) => p.lat != null);
      const maxValue = Math.max(...ranked.map((p) => this.metricValue(p)), 1);

      if (this.showRadius) {
        if (this.showHeroes) {
          this.heroes.forEach((h) => {
            if (h.lat == null || !h.radius_km) return;
            L.circle([h.lat, h.lon], {
              radius: h.radius_km * 1000,
              className: 'pm-radius pm-radius-hero',
              interactive: false,
            }).addTo(this.radiusLayer);
          });
        }
        if (this.showPartners) {
          ranked.forEach((p) => {
            if (!p.service_area_km) return;
            L.circle([p.lat, p.lon], {
              radius: p.service_area_km * 1000,
              className: 'pm-radius pm-radius-partner',
              interactive: false,
            }).addTo(this.radiusLayer);
          });
        }
      }

      if (this.showPartners && this.showRest) {
        this.restPartners.forEach((p) => {
          if (p.lat == null) return;
          const m = L.circleMarker([p.lat, p.lon], {
            radius: 3.5,
            className: `pm-dot pm-dot-rest${this.matches(p) ? '' : ' pm-dim'}`,
          })
            .addTo(this.markerLayer)
            .bindTooltip(this.partnerTooltip(p), {
              className: 'pm-tip',
              sticky: true,
            })
            .on('click', () =>
              this.$emit('select', { kind: 'partner', ref: p }),
            );
          if (this.showLabels) this.labelFor(m, shortName(p.name));
        });
      }

      if (this.showPartners) {
        ranked.forEach((p) => {
          const r = this.dotRadius(p, maxValue);
          const selected = this.selection && this.selection.ref === p;
          if (p.child_accounts) {
            L.circleMarker([p.lat, p.lon], {
              radius: r + 3.5,
              className: `pm-grp-ring${this.matches(p) ? '' : ' pm-dim'}`,
              interactive: false,
            }).addTo(this.markerLayer);
          }
          if (selected) {
            L.circleMarker([p.lat, p.lon], {
              radius: r + 7,
              className: 'pm-sel-ring',
              interactive: false,
            }).addTo(this.labelLayer);
          }
          const m = L.circleMarker([p.lat, p.lon], {
            radius: r,
            className: `pm-dot pm-dot-partner${
              this.matches(p) ? '' : ' pm-dim'
            }`,
          })
            .addTo(this.markerLayer)
            .bindTooltip(this.partnerTooltip(p), {
              className: 'pm-tip',
              sticky: true,
            })
            .on('click', () =>
              this.$emit('select', { kind: 'partner', ref: p }),
            );
          if (this.showLabels) this.labelFor(m, shortName(p.name));
          if (this.showRatings) {
            L.marker([p.lat, p.lon], {
              interactive: false,
              icon: L.divIcon({
                className: 'pm-rating-label',
                iconSize: [90, 16],
                iconAnchor: [45, -r - 2],
                html:
                  p.rating_avg == null
                    ? `<span class="none">${this.$t(
                        'partner-map.map.unrated',
                      )}</span>`
                    : `<span class="star">★</span>${this.fmt.dec(
                        p.rating_avg,
                      )} (${p.rating_count})`,
              }),
            }).addTo(this.labelLayer);
          }
        });
      }

      if (this.showHeroes) {
        this.heroes.forEach((h) => {
          if (h.lat == null) return;
          const selected = this.selection && this.selection.ref === h;
          if (selected) {
            L.circleMarker([h.lat, h.lon], {
              radius: HERO_R_100 + 6,
              className: 'pm-sel-ring',
              interactive: false,
            }).addTo(this.labelLayer);
          }
          const m = L.marker([h.lat, h.lon], {
            icon: this.heroIcon(h),
            riseOnHover: true,
          })
            .addTo(this.markerLayer)
            .bindTooltip(this.heroTooltip(h), {
              className: 'pm-tip',
              sticky: true,
            })
            .on('click', () => this.$emit('select', { kind: 'hero', ref: h }));
          if (this.showLabels) {
            this.labelFor(
              m,
              `${h.first_name} ${String(h.last_name || '').charAt(0)}.`,
            );
          }
        });
      }

      // Radius circles must never sit on top of the marks they belong to.
      this.radiusLayer.eachLayer((l) => l.bringToBack && l.bringToBack());
    },
  },
};
</script>

<style lang="scss">
.pm-canvas {
  position: relative;

  .pm-map {
    height: min(72vh, 780px);
    border-radius: 6px;
    background: #f4f4f2;
    z-index: 0;
  }

  // Desaturate OSM's default tiles into a quiet grey basemap so the data marks lead.
  .leaflet-tile-pane {
    filter: grayscale(1) contrast(0.82) brightness(1.09);
  }

  .pm-map-tools {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 500;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}

.pm-dot {
  stroke: #fff;
  stroke-width: 1.4;
  cursor: pointer;
}
.pm-dot-partner {
  fill: var(--pm-partner);
  fill-opacity: 0.88;
}
.pm-dot-rest {
  fill: var(--pm-ink-3);
  fill-opacity: 0.55;
  stroke-opacity: 0.6;
}
.pm-grp-ring {
  fill: none;
  stroke: var(--pm-partner);
  stroke-width: 1.2;
  stroke-dasharray: 3 3;
  stroke-opacity: 0.8;
}
.pm-sel-ring {
  fill: none;
  stroke: var(--pm-ink-1);
  stroke-width: 2;
  stroke-opacity: 0.85;
}
.pm-radius {
  fill-opacity: 0.05;
  stroke-width: 1;
  stroke-opacity: 0.45;
}
.pm-radius-partner {
  fill: var(--pm-partner);
  stroke: var(--pm-partner);
}
.pm-radius-hero {
  fill: var(--pm-hero);
  stroke: var(--pm-hero);
}
.pm-dim {
  opacity: 0.22;
}

.pm-hero-icon {
  cursor: pointer;

  .pm-hero-frame {
    fill: var(--pm-hero);
    fill-opacity: 0.1;
    stroke: var(--pm-hero);
    stroke-width: 1.2;
    stroke-opacity: 0.75;
  }
  .pm-hero-nodata {
    stroke-dasharray: 2.5 2.5;
  }
  .pm-hero-fill {
    fill: var(--pm-hero);
    stroke: #fff;
    stroke-width: 0.8;
  }
  .pm-hero-fill.over {
    fill: var(--pm-crit);
  }
}

.leaflet-tooltip.pm-label {
  background: rgba(255, 255, 255, 0.88);
  border: 0;
  box-shadow: none;
  padding: 0 3px;
  font-size: 10.5px;
  font-weight: 600;
  color: var(--pm-ink-2);
  white-space: nowrap;

  &::before {
    display: none;
  }
}

.pm-rating-label {
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
  text-align: center;
  color: var(--pm-ink-2);
  text-shadow: 0 0 3px #fff, 0 0 3px #fff;
  pointer-events: none;

  .star {
    color: var(--pm-warn);
  }
  .none {
    color: var(--pm-ink-3);
  }
}

.leaflet-tooltip.pm-tip {
  // Leaflet's base rule is `white-space: nowrap`, which makes max-width inert — long
  // partner names and account labels ran straight out of the box. Wrapping has to be
  // re-enabled here for the width to mean anything.
  white-space: normal;
  width: max-content;
  max-width: 280px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  border: 1px solid var(--pm-border);
  border-radius: 5px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);

  .t {
    font-weight: 700;
    margin-bottom: 4px;
    // A single unbroken token (a long domain-style account name) would otherwise still
    // push the box open past its max-width.
    overflow-wrap: anywhere;
  }
  .t-note {
    color: var(--pm-ink-3);
    font-style: italic;
    margin-bottom: 4px;
  }
  dl {
    display: grid;
    // minmax(0, …) lets both tracks shrink below their content width so the grid honours
    // the max-width instead of forcing the box wider; `auto` alone cannot shrink.
    grid-template-columns: minmax(0, auto) minmax(0, auto);
    gap: 1px 12px;
    margin: 0;
  }
  dt {
    font-weight: 400;
    color: var(--pm-ink-2);
    overflow-wrap: anywhere;
  }
  dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
}
</style>
